import React, { useCallback, useMemo } from 'react'
import { Form, Input, Typography, App } from 'antd'
import { EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useAuth } from '@/libs/context/auth'
import AuthService from '@/libs/services/auth'
import StorageService from '@/libs/services/storage'
import AuthSubmitButton from '@/features/auth/components/AuthSubmitButton'
import {
  AuthForm,
  AuthLink,
  AuthFooterText,
  AuthTitleWrapper,
  AuthLabelAsterisk,
  AuthFormItemSubmit,
} from '@/features/auth/components/AuthFormStyles'

const { Title } = Typography

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.md};
`

const LoginView = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { login } = useAuth()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const onFinish = useCallback(
    async values => {
      try {
        const response = await AuthService.submitLogin({
          email: values.email,
          password: values.password,
        })

        const { access, refresh } = response.data

        StorageService.updateUserToken(access)
        StorageService.updateRefreshToken(refresh)

        login({
          email: values.email,
          token: access,
          refresh: refresh,
        })

        notification.success({
          title: t('common.successTitle'),
          description: t('auth.login.success'),
          placement: 'topRight',
          duration: 3,
        })

        navigate('/dashboard')
      } catch {
        notification.error({
          title: t('common.errorTitle'),
          description: t('auth.login.errorInvalid'),
          placement: 'topRight',
          duration: 3,
        })
        form.setFields([
          {
            name: 'password',
            errors: [t('auth.login.errorInvalid')],
          },
        ])
      }
    },
    [form, login, navigate, notification, t]
  )

  const formRules = useMemo(
    () => ({
      email: [
        { required: true, message: t('auth.login.passwordRequired') },
        { type: 'email', message: t('auth.login.invalidEmailAddress') },
      ],
      password: [{ required: true, message: t('auth.login.passwordRequired') }],
    }),
    [t]
  )

  return (
    <>
      <AuthTitleWrapper>
        <Title level={3}>{t('auth.login.mainTitle')}</Title>
      </AuthTitleWrapper>

      <AuthForm form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <Form.Item
          name="email"
          rules={formRules.email}
          validateTrigger="onBlur"
          label={
            <>
              <AuthLabelAsterisk>*</AuthLabelAsterisk> {t('auth.login.email')}
            </>
          }
        >
          <Input placeholder={t('auth.login.email')} size="large" />
        </Form.Item>
        <Form.Item
          name="password"
          rules={formRules.password}
          validateTrigger="onBlur"
          label={
            <>
              <AuthLabelAsterisk>*</AuthLabelAsterisk> {t('auth.login.password')}
            </>
          }
        >
          <Input.Password
            placeholder="••••••••••••"
            size="large"
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <FormFooter>
          <AuthLink to="/auth/forgot-password">{t('auth.login.passwordReset')}</AuthLink>
        </FormFooter>

        <AuthFormItemSubmit>
          <AuthSubmitButton type="primary" htmlType="submit" block>
            {t('auth.login.submit')}
          </AuthSubmitButton>
        </AuthFormItemSubmit>
      </AuthForm>

      <AuthFooterText>
        {t('auth.login.subtitle')} <AuthLink to="/auth/register">{t('auth.login.create')}</AuthLink>
      </AuthFooterText>
    </>
  )
}

export default LoginView
